import { useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import SwitchVertical01 from "@untitled-ui/icons-react/build/esm/SwitchVertical01";
import { useSelector } from "react-redux";

import { Pagewrapper } from "../../components";
import PDLTypography from "../../components/dna/_pdltypography";
import { Tabs } from "../../components/_tab";
import { SearchInput } from "../../components/global/_searchInput";
import { getLoggedUser } from "../../../store/selectors";
import { getSavedItems, getSavedSearches } from "../../../services";

import SavedItemsList from "./_savedItemsList";
import SavedSearchesList from "./_savedSearchesList";
import { SortOrder } from "../../components/_saveDateFilter";
import "./index.scss";

type ProfileActiveTab = "saved" | "savedSearches";

interface ProfileProps {
  children: any;
  footerClass?: null;
  fullHeight?: boolean;
  headerClass?: null;
  id: string;
  isModule: boolean;
  name: string;
  protectedPage?: boolean;
  showFooter?: boolean;
  showHeader?: boolean;
}

const Profile = (props: ProfileProps) => {
  const [activeTab, setActiveTab] = useState<ProfileActiveTab>("saved");
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<SortOrder>(null);
  const [savedItems, setSavedItems] = useState<any[]>([]);
  const [savedSearches, setSavedSearches] = useState<any[]>([]);
  const [_loading, setLoading] = useState(false);
  const loggedUser = useSelector(getLoggedUser);

  const userName = loggedUser?.name || "Usuário";
  const fetchSavedItems = async () => {
    setLoading(true);
    try {
      const items = await getSavedItems(order);
      setSavedItems(items);
    } catch (error) {
      console.error("Error fetching saved items:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchSavedSearches = async () => {
    if (activeTab === "savedSearches") {
      setLoading(true);
      try {
        const searches = await getSavedSearches();
        setSavedSearches(searches);
      } catch (error) {
        console.error("Error fetching saved searches:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (activeTab === "saved") {
      fetchSavedItems();
    } else if (activeTab === "savedSearches") {
      fetchSavedSearches();
    }
  }, [order, activeTab]);

  const results = useMemo(() => {
    if (activeTab === "savedSearches") {
      return savedSearches;
    }

    if (search) {
      return savedItems.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase())
      );
    }

    return savedItems;
  }, [activeTab, search, savedItems, savedSearches]);

  return (
    <Pagewrapper {...props}>
      <Container>
        <Row>
          <Col className="mt-5">
            <PDLTypography size="xs" weight="semibold">
              Olá, {userName}!
            </PDLTypography>

            <PDLTypography size="md">
              Veja os seus itens salvos e o histórico de pesquisa.
            </PDLTypography>

            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as ProfileActiveTab)}
              tabs={[
                { key: "saved", label: "Salvos" },
                { key: "savedSearches", label: "Pesquisas salvas" },
              ]}
            />
          </Col>
        </Row>

        {activeTab === "saved" && (
          <Row className="pt-4 align-items-center justify-content-between saved-items-header">
            <Col xs={12} md="auto" className="mb-3 mb-md-0 profile-search-col">
              <SearchInput
                value={search}
                onChangeText={setSearch}
                placeholder="Pesquisar em salvos"
              />
            </Col>

            <Col
              xs={12}
              md="auto"
              className="d-flex justify-content-md-end profile-sort-col"
            >
              <DropdownButton
                variant="outline-secondary"
                title={
                  <>
                    <SwitchVertical01 className="me-2" />
                    {order === "desc" ? "Mais recentes" : "Mais antigos"}
                  </>
                }
                id="dropdown-sort-options"
              >
                <Dropdown.Item onClick={() => setOrder("desc")}>
                  Mais recentes
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setOrder("asc")}>
                  Mais antigos
                </Dropdown.Item>
              </DropdownButton>
            </Col>
          </Row>
        )}

        <Row className="mt-4">
          <Col>
            {activeTab === "saved" ? (
              <SavedItemsList
                items={results}
                searchTerm={search}
                onRemove={(item) => {
                  console.log("Remover item:", item);
                  fetchSavedItems();
                }}
              />
            ) : (
              <SavedSearchesList
                searches={results as any}
                onDeleteAll={() => {
                  console.log("Apagar todas as pesquisas");
                }}
                onDelete={(id) => {
                  console.log("Apagar pesquisa:", id);
                }}
                onSortChange={setOrder}
              />
            )}
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default Profile;
